import { useMutation } from '@tanstack/react-query';
import { Bus, Clock, Footprints, Route, Search, SearchX, Timer } from 'lucide-react';
import { type ComponentType, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { toast } from 'sonner';
import { HeatmapLayers } from '@/components/map/heatmap-layers';
import { fc, GeoJsonLayer, lineLayer } from '@/components/map/layers';
import { MapView } from '@/components/map/map-view';
import { PointPicker } from '@/components/map/point-picker';
import { DailyBars } from '@/components/stats';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Switch } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime, timeAgo } from '@/lib/format';
import { locale, t, tn } from '@/lib/i18n';
import {
  type Area,
  decodePolyline,
  type PlanLogItem,
  type PlannerSettings,
  type PlannerStats,
  type TryItinerary,
  usePlannerRecent,
  usePlannerSettings,
  usePlannerStats,
  usePlannerStatus,
  usePlannerStorage,
  useSavePlannerSettings,
} from '@/lib/planner';

const num = (n: number) => n.toLocaleString(locale);
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');
const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
const bytes = (b: number | null) =>
  b == null ? '—' : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`;
const areaName = (a: Area) => a.name ?? `${a.lat.toFixed(3)}, ${a.lng.toFixed(3)}`;

const TABS: Array<[string, string]> = [
  ['/planner', t('Overview')],
  ['/planner/usage', t('Use in the app')],
  ['/planner/trips', t('Trips')],
  ['/planner/settings', t('Settings')],
];

/**
 * The route planner, in tabs: how it is doing (overview), how people use it, trips (the last ones
 * and a trial on the map) and its settings.
 */
export function PlannerLayout() {
  return (
    <>
      <PageHeader
        title={t('Route planner')}
        subtitle={t('How trips are planned in the app, what the planner has loaded, and what people ask it.')}
        actions={<StatusBadge />}
      />
      <nav className="mb-6 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            end
            className={({ isActive }) =>
              `-mb-px border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap ${isActive ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}

/** At a glance: last week in numbers, what is loaded, what it cannot use, what is stored. */
export function PlannerOverview() {
  return (
    <div className="space-y-6">
      <UsageTiles days={7} />
      <div className="grid items-start gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <StatusCard />
          <StorageCard />
        </div>
        <ChecksCard />
      </div>
    </div>
  );
}

export function PlannerUsage() {
  const [days, setDays] = useState(30);
  return (
    <div className="space-y-6">
      <UsageTiles days={days} setDays={setDays} />
      <UsageSection days={days} />
    </div>
  );
}

export function PlannerTrips() {
  const [trial, setTrial] = useState<{ from: [number, number]; to: [number, number] } | null>(null);
  return (
    <div className="space-y-6">
      <TryCard trial={trial} />
      <RecentCard onTry={(it) => setTrial({ from: [it.from[1], it.from[0]], to: [it.to[1], it.to[0]] })} />
    </div>
  );
}

export function PlannerSettingsPage() {
  return (
    <div className="max-w-3xl">
      <SettingsCard />
    </div>
  );
}

function StatusBadge() {
  const status = usePlannerStatus();
  const s = status.data;
  if (!s) return null;
  if (!s.up) return <Badge tone="red">{t('Planner not reachable')}</Badge>;
  if (s.settings && !s.settings.enabled) return <Badge tone="amber">{t('Turned off')}</Badge>;
  if (!s.loaded_at) return <Badge tone="amber">{t('Loading data…')}</Badge>;
  if (!s.walk) return <Badge tone="amber">{t('Running · walking network loading')}</Badge>;
  return <Badge tone="green">{t('Running')}</Badge>;
}

function Tile({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Icon className="size-4" /> {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function UsageTiles({ days, setDays }: { days: number; setDays?: (n: number) => void }) {
  const stats = usePlannerStats(days);
  const s = stats.data?.totals;
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">
          {setDays ? t('Use in the app') : t('Last {n} days in the app', { n: days })}
        </h2>
        {setDays ? (
          <Select
            className="w-40"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label={t('Period')}
          >
            {[1, 7, 30, 90].map((n) => (
              <option key={n} value={n}>
                {n === 1 ? t('Today') : t('Last {n} days', { n })}
              </option>
            ))}
          </Select>
        ) : (
          <Link to="/planner/usage" className="text-sm font-medium text-brand-700 hover:underline">
            {t('More about use')} →
          </Link>
        )}
      </div>
      {!s ? (
        <Spinner />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Tile
            icon={Route}
            label={t('Trips planned')}
            value={num(s.n)}
            sub={t('{n} per day', { n: num(Math.round(s.n / days)) })}
          />
          <Tile
            icon={SearchX}
            label={t('No way found')}
            value={pct(s.empty, s.n)}
            sub={t('{n} trips · {f} failed', { n: num(s.empty), f: num(s.failed) })}
          />
          <Tile
            icon={Timer}
            label={t('Fastest way, on average')}
            value={
              s.n - s.empty - s.failed
                ? t('{n} min', { n: Math.round(s.minutes / (s.n - s.empty - s.failed)) })
                : '—'
            }
            sub={t('answered in {n} ms on average', { n: s.n ? Math.round(s.ms / s.n) : '—' })}
          />
          <Tile
            icon={Search}
            label={t('Place searches')}
            value={num(s.searches)}
            sub={t('{p} found nothing', { p: pct(s.searches_empty, s.searches) })}
          />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ settings

const FIELDS: Array<{
  key: keyof PlannerSettings;
  label: string;
  unit: string;
  hint: string;
  step?: number;
}> = [
  {
    key: 'transfer_max_m',
    label: t('Longest walk between stations'),
    unit: 'm',
    hint: t('When changing buses, walked along the streets.'),
  },
  {
    key: 'min_transfer_sec',
    label: t('Time to change buses'),
    unit: 's',
    hint: t('On top of the walk: the next bus must leave at least this much later.'),
  },
  {
    key: 'max_transfers',
    label: t('Most changes in a trip'),
    unit: '',
    hint: t('0: direct buses only. At most 3.'),
  },
  {
    key: 'access_max_m',
    label: t('Longest walk to the first stop'),
    unit: 'm',
    hint: t('And from the last stop. With no stop that close, the 3 nearest within 2 km.'),
  },
  {
    key: 'walk_only_always_m',
    label: t('Walking alone, always offered up to'),
    unit: 'm',
    hint: t('Shown next to the buses, even when a bus is faster.'),
  },
  {
    key: 'walk_only_max_m',
    label: t('Walking alone, never beyond'),
    unit: 'm',
    hint: t('Between the two, walking is offered only when no bus is faster.'),
  },
  {
    key: 'walk_speed_kmh',
    label: t('Walking speed'),
    unit: 'km/h',
    hint: t('4.5 km/h is a usual pace; the app shows walking times with it.'),
    step: 0.1,
  },
  {
    key: 'walk_reluctance',
    label: t('How much walking is avoided'),
    unit: '×',
    hint: t('Walking minutes count this many times when ways are compared. 1: walking is as good as riding.'),
    step: 0.1,
  },
];

function SettingsCard() {
  const { can } = useAuth();
  const q = usePlannerSettings();
  const save = useSavePlannerSettings();
  const [form, setForm] = useState<PlannerSettings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data && !form) {
      const { updated_at: _, ...s } = q.data.settings;
      setForm(s);
    }
  }, [q.data, form]);
  if (!q.data || !form)
    return (
      <Card title={t('Settings')}>
        <Spinner />
      </Card>
    );
  const readOnly = !can('admin');
  const defaults = q.data.defaults;
  const set = <K extends keyof PlannerSettings>(k: K, v: PlannerSettings[K]) => setForm({ ...form, [k]: v });
  const submit = () => {
    setErrors({});
    save.mutate(form, {
      onError: (err) => {
        if (err instanceof ApiError && err.fields) setErrors(err.fields);
      },
    });
  };
  return (
    <Card
      title={t('Settings')}
      actions={
        q.data.settings.updated_at && (
          <span className="text-xs text-slate-500">
            {t('Changed {time}', { time: timeAgo(q.data.settings.updated_at) })}
          </span>
        )
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Switch
            checked={form.enabled}
            onChange={(v) => !readOnly && set('enabled', v)}
            label={t('Trip planning in the app')}
          />
          <Switch
            checked={form.log_requests}
            onChange={(v) => !readOnly && set('log_requests', v)}
            label={t('Count the trips asked for')}
          />
        </div>
        {!form.enabled && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            {t('Off: the app says trip planning is not available right now. Place search still works.')}
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              error={errors[f.key]}
              hint={
                <>
                  {f.hint}{' '}
                  {form[f.key] !== defaults[f.key] && (
                    <span className="text-slate-400">
                      {t('Default: {v}', { v: `${defaults[f.key]}${f.unit ? ` ${f.unit}` : ''}` })}
                    </span>
                  )}
                </>
              }
            >
              {(id) => (
                <div className="flex items-center gap-2">
                  <Input
                    id={id}
                    type="number"
                    step={f.step ?? 1}
                    disabled={readOnly}
                    className="w-28"
                    invalid={!!errors[f.key]}
                    value={String(form[f.key])}
                    onChange={(e) => set(f.key, Number(e.target.value) as never)}
                  />
                  <span className="text-sm text-slate-500">{f.unit}</span>
                </div>
              )}
            </Field>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          {t(
            'Only rounded places (≈ 100 m) and counts are kept: per hour, per area for 90 days, and the last 1000 trips. Nothing that identifies a phone or a person.',
          )}
        </p>
        {!readOnly && (
          <div className="flex gap-2">
            <Button loading={save.isPending} onClick={submit}>
              {t('Save')}
            </Button>
            <Button variant="secondary" onClick={() => setForm({ ...defaults })}>
              {t('Back to defaults')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------ status

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5 last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-800">{children}</dd>
    </div>
  );
}

function StatusCard() {
  const q = usePlannerStatus();
  const s = q.data;
  return (
    <Card title={t('What the planner has loaded')}>
      {!s ? (
        <Spinner />
      ) : !s.up ? (
        <p className="text-sm text-red-700">
          {t('The planner service cannot be reached. The app shows that trip planning is not available.')}
        </p>
      ) : (
        <dl className="text-sm">
          <Row label={t('Running since')}>{formatDateTime(s.started_at)}</Row>
          <Row label={t('Network and timetables read')}>
            {s.loaded_at ? `${timeAgo(s.loaded_at)} · ${t('{n} ms', { n: s.load_ms ?? 0 })}` : t('Loading…')}
          </Row>
          <Row label={t('Read again')}>{s.next_load_at ? clock(s.next_load_at) : '—'}</Row>
          {s.network && (
            <>
              <Row label={t('Lines · directions · stations')}>
                {`${s.network.lines} · ${s.network.directions} · ${s.network.stations}`}
              </Row>
              <Row label={t('Directions with a timetable')}>
                {t('{w} weekdays · {e} weekend', { w: s.network.planned.week, e: s.network.planned.weekend })}
              </Row>
              <Row label={t('Bus trips per day')}>
                {s.trips ? t('{wd} weekday · {sat} Sat · {sun} Sun', s.trips) : '—'}
              </Row>
              <Row label={t('Times between stops, measured')}>
                {`${pct(s.network.segments.measured, s.network.segments.total)} (${num(s.network.segments.measured)} / ${num(s.network.segments.total)})`}
              </Row>
            </>
          )}
          <Row label={t('Walking network (OpenStreetMap)')}>
            {s.walk
              ? `${t('{n} points', { n: num(s.walk.nodes) })} · ${timeAgo(s.walk.built_at!)}`
              : t('Loading… walks are estimated until then')}
          </Row>
          <Row label={t('Walking changes between stations')}>
            {s.transfers
              ? `${tn(s.transfers.pairs, '{n} pair', '{n} pairs')} · ${s.transfers.streets ? t('along the streets') : t('estimated')}`
              : '—'}
          </Row>
          <Row label={t('Places to search')}>
            {s.places
              ? `${num(s.places.indexed)}${s.places.osm_built_at ? ` · ${t('OSM {time}', { time: timeAgo(s.places.osm_built_at) })}` : ''}`
              : '—'}
          </Row>
          {s.requests && (
            <Row label={t('Answers since start')}>
              {s.requests.count
                ? t('{n} · usually {p50} ms, slow {p95} ms', {
                    n: num(s.requests.count),
                    p50: s.requests.p50_ms ?? '—',
                    p95: s.requests.p95_ms ?? '—',
                  })
                : t('None yet')}
            </Row>
          )}
          {s.memory_mb && <Row label={t('Memory')}>{`${s.memory_mb.rss} MB`}</Row>}
          <Row label={t('Settings read')}>{s.settings_read_at ? timeAgo(s.settings_read_at) : '—'}</Row>
        </dl>
      )}
    </Card>
  );
}

function ChecksCard() {
  const q = usePlannerStatus();
  const n = q.data?.network;
  const off = q.data?.walk?.off_network ?? [];
  return (
    <Card title={t('What the planner cannot use')}>
      {!q.data ? (
        <Spinner />
      ) : !n ? (
        <p className="text-sm text-slate-500">{t('Shown once the planner has loaded.')}</p>
      ) : (
        <div className="space-y-4 text-sm">
          <section>
            <h3 className="mb-1 font-medium text-slate-800">
              {tn(n.missing.length, '{n} direction without departures', '{n} directions without departures')}
            </h3>
            <p className="mb-2 text-xs text-slate-500">
              {t('No first-stop times in the timetable: no trips are planned on them on those days.')}
            </p>
            <ul className="max-h-48 space-y-1 overflow-auto">
              {n.missing.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2">
                  <Link to={`/schedules/directions/${m.id}`} className="text-brand-700 hover:underline">
                    {m.sign} → {m.headsign}
                  </Link>
                  <span className="text-xs text-slate-500">
                    {[!m.week && t('weekdays'), !m.weekend && t('weekend')].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="mb-1 font-medium text-slate-800">
              {tn(n.unserved.length, '{n} station no bus stops at', '{n} stations no bus stops at')}
            </h3>
            <p className="mb-2 text-xs text-slate-500">{t('Not on any direction that has departures.')}</p>
            <StationList items={n.unserved} />
          </section>
          <section>
            <h3 className="mb-1 font-medium text-slate-800">
              {tn(off.length, '{n} station away from the streets', '{n} stations away from the streets')}
            </h3>
            <p className="mb-2 text-xs text-slate-500">
              {t(
                'More than 150 m from any walkable street in OpenStreetMap: walks to them are straight lines. Check the position.',
              )}
            </p>
            <StationList items={off.map((o) => ({ ...o, extra: o.m == null ? '—' : `${o.m} m` }))} />
          </section>
        </div>
      )}
    </Card>
  );
}

function StationList({ items }: { items: Array<{ id: string; name: string; extra?: string }> }) {
  if (!items.length) return <p className="text-xs text-emerald-700">{t('None')}</p>;
  return (
    <ul className="max-h-40 space-y-1 overflow-auto">
      {items.map((s) => (
        <li key={s.id} className="flex justify-between gap-2">
          <Link to={`/stations/${s.id}`} className="text-brand-700 hover:underline">
            {s.name || s.id}
          </Link>
          {s.extra && <span className="text-xs text-slate-500">{s.extra}</span>}
        </li>
      ))}
    </ul>
  );
}

const COLLECTION_LABELS: Record<string, string> = {
  osm_places: t('Places from OpenStreetMap'),
  segment_times: t('Measured times between stops'),
  stat_plan_hours: t('Trips per hour (kept 2 years)'),
  stat_plan_pairs: t('Trips per area and day (kept 90 days)'),
  stat_place_misses: t('Searches with no result (kept 90 days)'),
  plan_log: t('Last trips (the last 1000)'),
};

function StorageCard() {
  const q = usePlannerStorage();
  return (
    <Card title={t('Stored for the planner')}>
      {!q.data ? (
        <Spinner />
      ) : (
        <div className="text-sm">
          <dl>
            {q.data.collections.map((c) => (
              <Row key={c.name} label={COLLECTION_LABELS[c.name] ?? c.name}>
                {`${num(c.count)} · ${bytes(c.bytes)}`}
              </Row>
            ))}
          </dl>
          <p className="mt-3 text-xs text-slate-500">
            {q.data.osm?.builtAt
              ? t(
                  'OpenStreetMap places read {time}; read again every week. The walking network stays in the planner’s memory.',
                  {
                    time: formatDateTime(new Date(q.data.osm.builtAt)),
                  },
                )
              : t('OpenStreetMap places are read soon after the planner starts.')}
          </p>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------ use in the app

function UsageSection({ days }: { days: number }) {
  const q = usePlannerStats(days);
  const s = q.data;
  const [layer, setLayer] = useState<'origins' | 'destinations' | 'no_route'>('origins');
  const cells = useMemo(() => {
    if (!s) return [];
    const rows =
      layer === 'no_route'
        ? s.destinations.filter((d) => d.empty > 0).map((d) => ({ ...d, n: d.empty }))
        : s[layer];
    return rows.map((r) => [r.lng, r.lat, r.n, 0] as [number, number, number, number]);
  }, [s, layer]);
  if (!s) return <Spinner />;
  const ways = s.totals;
  const found = ways.n - ways.empty - ways.failed;
  const maxHour = Math.max(1, ...s.by_hour.map((h) => h.n));
  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-3">
        <Card title={t('Trips planned per day')} className="xl:col-span-2">
          <DailyBars data={s.by_day.map((d) => ({ day: d.d, count: d.n }))} />
        </Card>
        <Card title={t('The fastest way found')}>
          <dl className="text-sm">
            <Row label={t('Direct bus')}>{pct(ways.direct, found)}</Row>
            <Row label={t('One change')}>{pct(ways.one_change, found)}</Row>
            <Row label={t('Two changes or more')}>{pct(ways.more_changes, found)}</Row>
            <Row label={t('Walking')}>{pct(ways.walk, found)}</Row>
            <Row label={t('First bus live (on its way)')}>{pct(ways.live, found - ways.walk)}</Row>
            <Row label={t('Arrive by a time')}>{pct(ways.arrive_by, ways.n)}</Row>
            <Row label={t('For later')}>{pct(ways.later, ways.n)}</Row>
          </dl>
        </Card>
      </div>

      <Card title={t('When trips are planned (hour of day)')}>
        <div className="flex h-28 items-end gap-1 border-b border-slate-200">
          {s.by_hour.map((h) => (
            <div key={h.h} className="group relative flex h-full flex-1 flex-col justify-end">
              <div
                className={`rounded-t ${h.n ? 'bg-brand-600/80 group-hover:bg-brand-700' : 'bg-slate-100'}`}
                style={{ height: h.n ? `${Math.max(4, (h.n / maxHour) * 100)}%` : '2px' }}
              />
              <div className="pointer-events-none absolute -top-7 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-slate-900 px-1.5 py-0.5 text-[11px] whitespace-nowrap text-white group-hover:block">
                {t('{h}:00 · {n} trips · {s} searches', {
                  h: String(h.h).padStart(2, '0'),
                  n: h.n,
                  s: h.searches,
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-slate-400">
          <span>00</span>
          <span>06</span>
          <span>12</span>
          <span>18</span>
          <span>23</span>
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card
          title={t('Where trips start and end')}
          padded={false}
          actions={
            <div className="flex rounded-md bg-slate-100 p-0.5 text-sm">
              {(
                [
                  ['origins', t('From')],
                  ['destinations', t('To')],
                  ['no_route', t('No way found')],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setLayer(k)}
                  className={`rounded px-3 py-1 ${layer === k ? 'bg-white font-medium shadow-sm' : 'text-slate-600'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        >
          <MapView className="h-[420px] rounded-none border-0">
            <HeatmapLayers kind="user" cells={cells} />
          </MapView>
        </Card>
        <div className="space-y-6">
          <PairsCard title={t('Most planned trips')} rows={s.top_pairs} />
          <PairsCard title={t('Where no way is found')} rows={s.no_route} empty />
        </div>
      </div>

      <Card title={t('Searched for, found nothing')}>
        {s.misses.length === 0 ? (
          <p className="text-sm text-slate-500">{t('None yet')}</p>
        ) : (
          <>
            <p className="mb-3 text-xs text-slate-500">
              {t(
                'What people look for that the search does not know: add it as a point of interest, or fix the name in OpenStreetMap.',
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {s.misses.map((m) => (
                <span key={m.q} className="rounded-md bg-slate-100 px-2 py-1 text-sm">
                  {m.q} <span className="text-slate-500">×{m.n}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function PairsCard({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: PlannerStats['top_pairs'];
  empty?: boolean;
}) {
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">{t('None yet')}</p>
      ) : (
        <ol className="space-y-1.5 text-sm">
          {rows.slice(0, 8).map((r) => (
            <li
              key={`${r.from.lat},${r.from.lng}-${r.to.lat},${r.to.lng}`}
              className="flex justify-between gap-3"
            >
              <span className="truncate">
                {areaName(r.from)} <span className="text-slate-400">→</span> {areaName(r.to)}
              </span>
              <span className="shrink-0 text-slate-500">{empty ? `${r.empty} / ${r.n}` : r.n}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function RecentCard({ onTry }: { onTry: (it: PlanLogItem) => void }) {
  const [empty, setEmpty] = useState(false);
  const q = usePlannerRecent(empty);
  return (
    <Card
      title={t('Last trips asked for')}
      padded={false}
      actions={<Switch checked={empty} onChange={setEmpty} label={t('Only with no way found')} />}
    >
      {!q.data ? (
        <Spinner />
      ) : q.data.items.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">{t('None yet')}</p>
      ) : (
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t('When')}</th>
                <th className="px-4 py-2 font-medium">{t('Fastest way')}</th>
                <th className="px-4 py-2 font-medium">{t('Minutes')}</th>
                <th className="px-4 py-2 font-medium">{t('Walking')}</th>
                <th className="px-4 py-2 font-medium">{t('Answer')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((it) => (
                <tr key={`${it.t}-${it.from.join()}-${it.to.join()}`} className="border-t border-slate-100">
                  <td className="px-4 py-1.5 whitespace-nowrap">
                    {clock(it.t)}
                    {it.arrive_by && <Badge tone="blue">{t('arrive by')}</Badge>}
                    {it.at && !it.arrive_by && (
                      <span className="ml-1 text-xs text-slate-500">
                        {t('for {time}', { time: clock(it.at) })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    {it.error ? (
                      <Badge tone="red">{t('failed')}</Badge>
                    ) : it.n === 0 ? (
                      <Badge tone="amber">{t('no way found')}</Badge>
                    ) : it.walk_only ? (
                      <span className="inline-flex items-center gap-1">
                        <Footprints className="size-3.5" /> {t('walking')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Bus className="size-3.5" /> {it.lines}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">{it.minutes ?? '—'}</td>
                  <td className="px-4 py-1.5">{it.walk_m != null ? `${it.walk_m} m` : '—'}</td>
                  <td className="px-4 py-1.5 text-slate-500">{it.ms} ms</td>
                  <td className="px-4 py-1.5 text-right">
                    <button
                      type="button"
                      className="text-brand-700 hover:underline"
                      onClick={() => onTry(it)}
                    >
                      {t('Try again')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------ try a trip

const WALK_COLOR = '#64748b';

function TryCard({ trial }: { trial: { from: [number, number]; to: [number, number] } | null }) {
  const [from, setFrom] = useState<[number, number] | null>(null);
  const [to, setTo] = useState<[number, number] | null>(null);
  const [chosen, setChosen] = useState(0);
  const run = useMutation({
    mutationFn: (p: { from: [number, number]; to: [number, number] }) =>
      api<{ itineraries: TryItinerary[]; ms: number }>('/planner/try', {
        method: 'POST',
        body: { from: { lat: p.from[1], lng: p.from[0] }, to: { lat: p.to[1], lng: p.to[0] } },
      }),
    onSuccess: () => setChosen(0),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not plan')),
  });
  // A trip from the list below: shown here and planned again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when another trip is picked
  useEffect(() => {
    if (!trial) return;
    setFrom(trial.from);
    setTo(trial.to);
    run.mutate(trial);
    document.getElementById('try-trip')?.scrollIntoView({ behavior: 'smooth' });
  }, [trial]);
  const pick = (p: [number, number]) => {
    if (!from || (from && to)) {
      setFrom(p);
      setTo(null);
      run.reset();
    } else {
      setTo(p);
      run.mutate({ from, to: p });
    }
  };
  const its = run.data?.itineraries ?? [];
  const it = its[chosen];
  const shapes = useMemo(
    () =>
      fc(
        (it?.legs ?? []).map((l) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: l.shape
              ? decodePolyline(l.shape)
              : [
                  [l.from.lng, l.from.lat],
                  [l.to.lng, l.to.lat],
                ],
          },
          properties: { color: l.mode === 'bus' ? (l.color ?? '#0b3f82') : WALK_COLOR },
        })),
      ),
    [it],
  );
  return (
    <div id="try-trip">
      <Card title={t('Try a trip')} padded={false}>
        <div className="grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <MapView className="h-[460px] rounded-none border-0">
            <GeoJsonLayer data={shapes} layers={[lineLayer(['get', 'color'], 5, 0.85)]} />
            <PointPicker value={from} onChange={pick} color="#0f172a" />
            <PointPicker value={to} onChange={() => {}} readOnly color="#E14032" />
          </MapView>
          <div className="max-h-[460px] space-y-3 overflow-auto p-4 text-sm">
            <p className="text-slate-500">
              {!from
                ? t('Click the map where the trip starts.')
                : !to
                  ? t('Now click where it ends.')
                  : t('Leaving now. Click the map again to start over.')}
            </p>
            {run.isPending && <Spinner label={t('Planning…')} />}
            {run.data && its.length === 0 && (
              <p className="rounded-md bg-amber-50 p-2 text-amber-800">
                {t(
                  'No way found. Is a stop within the walking distance of both places, and does a bus run now?',
                )}
              </p>
            )}
            {its.map((x, i) => (
              <button
                key={`${x.depart}-${x.legs.map((l) => l.sign ?? 'w').join('-')}`}
                type="button"
                onClick={() => setChosen(i)}
                className={`block w-full rounded-md border p-2.5 text-left ${i === chosen ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <div className="flex justify-between font-medium">
                  <span>
                    {clock(x.depart)} – {clock(x.arrive)}
                  </span>
                  <span>{t('{n} min', { n: x.minutes })}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600">
                  {x.legs.map((l) =>
                    l.mode === 'bus' ? (
                      <span
                        key={`${l.mode}-${l.depart}`}
                        className="rounded px-1.5 py-0.5 font-semibold text-white"
                        style={{ background: l.color ?? '#0b3f82' }}
                      >
                        {l.sign}
                      </span>
                    ) : (
                      <span key={`${l.mode}-${l.depart}`} className="inline-flex items-center gap-0.5">
                        <Footprints className="size-3" />
                        {l.distance_m} m{l.shape ? '' : '*'}
                      </span>
                    ),
                  )}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {tn(x.transfers, '{n} change', '{n} changes')} · {t('{n} m walking', { n: x.walk_m })}
                </div>
              </button>
            ))}
            {run.data && (
              <p className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="size-3" />
                {t('Answered in {n} ms. * walk estimated (no street path).', { n: run.data.ms })}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
